/**
 * Cognito token exchange (phase 2). Uses native fetch with URLSearchParams because
 * the OAuth token endpoint requires application/x-www-form-urlencoded.
 *
 * `refreshTokens` performs the refresh_token grant against the Hosted UI
 * `/oauth2/token`. A second implementation using the regional Cognito IDP
 * `InitiateAuth` was removed: the corporate proxy blocks
 * `cognito-idp.<region>.amazonaws.com`, so it could only fail with a CORS error.
 */

import { getCognitoConfig } from './config';
import { getStoredIdToken, decodeJwtPayload } from './session';

/**
 * App client that issued the current session. The id_token `aud` claim wins over the
 * compiled config so the refresh grant always targets the pool that issued the tokens,
 * even if this bundle's config drifts (e.g. a placeholder PROD entry).
 */
const resolveClientId = (cfg) => {
  const claims = decodeJwtPayload(getStoredIdToken());
  const aud = claims && (typeof claims.aud === 'string' ? claims.aud : (claims.aud || [])[0]);
  return aud || (cfg && cfg.clientId) || '';
};

const parseOAuthErrorBody = async (response) => {
  let text = '';
  try {
    text = await response.text();
  } catch (e) {
    return {};
  }

  if (!text) {
    return {};
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {};
  }

  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  return {
    error: typeof parsed.error === 'string' ? parsed.error : undefined,
    error_description: typeof parsed.error_description === 'string' ? parsed.error_description : undefined,
  };
};

const parseOAuthTokenBody = async (response) => {
  let text = '';
  try {
    text = await response.text();
  } catch (e) {
    return {};
  }

  if (!text) {
    return {};
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {};
  }

  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  const expiresIn = Number(parsed.expires_in);

  return {
    id_token: typeof parsed.id_token === 'string' ? parsed.id_token : undefined,
    access_token: typeof parsed.access_token === 'string' ? parsed.access_token : undefined,
    refresh_token: typeof parsed.refresh_token === 'string' ? parsed.refresh_token : undefined,
    token_type: typeof parsed.token_type === 'string' ? parsed.token_type : undefined,
    expires_in: Number.isFinite(expiresIn) ? expiresIn : undefined,
    scope: typeof parsed.scope === 'string' ? parsed.scope : undefined,
  };
};

/** Exchange an authorization code for tokens (Authorization Code + PKCE). */
export const exchangeCodeForTokens = async ({ code, codeVerifier }) => {
  const cfg = getCognitoConfig();

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.clientId,
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: codeVerifier,
  });

  const response = await fetch(`${cfg.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorBody = await parseOAuthErrorBody(response);
    const error = new Error(
      errorBody.error_description || errorBody.error || `Token exchange failed (${response.status})`
    );
    error.status = response.status;
    error.body = errorBody;
    throw error;
  }

  return parseOAuthTokenBody(response);
};

/** Exchange a refresh_token for fresh tokens (grant_type=refresh_token). Public client (PKCE, no secret). */
export const refreshTokens = async ({ refreshToken }) => {
  const cfg = getCognitoConfig();
  const clientId = resolveClientId(cfg);

  if (!cfg.domain || !clientId) {
    const error = new Error('Cognito domain/clientId is not configured');
    error.status = 0;
    throw error;
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(`${cfg.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorBody = await parseOAuthErrorBody(response);
    const error = new Error(
      errorBody.error_description || errorBody.error || `Token refresh failed (${response.status})`
    );
    error.status = response.status;
    error.body = errorBody;
    // The refresh_token itself is expired/revoked: no other path can recover it.
    error.fatal = errorBody.error === 'invalid_grant';
    throw error;
  }

  return parseOAuthTokenBody(response);
};

export default exchangeCodeForTokens;
